import { Tooltip } from '@progress/kendo-react-tooltip'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { HStack } from '~/components/elements/HStack'
import { StyledButton } from '~/components/elements/StyledButton'
import { StyledInput } from '~/components/elements/StyledInput'
import { useBrandAccountAiEnabledQuery } from '~/hooks/useBrandAccountAiEnabledQuery'
import { useCommonQueryCurrentBrandAccount } from '~/hooks/useCommonQuery/useCommonQueryCurrentBrandAccount'

type DocumentQaFormProps = {
  hintMessage?: string
  initialQuestion?: string
  isDisabled: boolean
  isLoading: boolean
  onSubmit: (question: string) => void
}

export const DocumentQaForm: React.FC<DocumentQaFormProps> = ({
  hintMessage,
  initialQuestion,
  isDisabled,
  isLoading,
  onSubmit,
}) => {
  const { t } = useTranslation()
  const [question, setQuestion] = React.useState(initialQuestion ?? '')

  React.useEffect(() => {
    setQuestion(initialQuestion ?? '')
  }, [initialQuestion])

  const handleSubmit = React.useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      if (question.trim().length > 0) {
        onSubmit(question.trim())
      }
    },
    [onSubmit, question],
  )

  const brandAccountId = useCommonQueryCurrentBrandAccount()?.id
  const { data: aiEnabledData, isPending } =
    useBrandAccountAiEnabledQuery(brandAccountId)
  // Only an explicit `false` is "AI off". A pending/errored lookup must NOT be
  // shown as AI-off (matches AnalysisCommentBlock: disable while pending, never
  // downgrade an error to "AI off").
  const aiDisabled = aiEnabledData?.brandAccount?.aiEnabled === false
  const inputDisabled =
    isDisabled || isLoading || !brandAccountId || isPending || aiDisabled

  const formContent = (
    <div
      style={{
        marginBottom: '24px',
        opacity: aiDisabled ? 0.5 : 1,
        pointerEvents: aiDisabled ? 'none' : 'auto',
      }}
    >
      <form style={{ display: 'flex', gap: '8px' }} onSubmit={handleSubmit}>
        {/* flex:1 wrapper lets the shared StyledInput fill the row like the
            previous raw input did (StyledInput sizes itself via width="100%"). */}
        <div style={{ flex: 1 }}>
          <StyledInput
            disabled={inputDisabled}
            placeholder={t('document_qa_placeholder')}
            value={question}
            width="100%"
            onValueChange={setQuestion}
          />
        </div>
        <HStack align="center" spacing={2}>
          <StyledButton
            disabled={inputDisabled || question.trim().length === 0}
            themeColor="primary"
            type="submit"
          >
            {isLoading ? t('document_qa_searching') : t('document_qa_search')}
          </StyledButton>
        </HStack>
      </form>
      {hintMessage && (
        <p style={{ color: '#888', fontSize: '12px', marginTop: '6px' }}>
          {hintMessage}
        </p>
      )}
    </div>
  )

  if (!aiDisabled) {
    return formContent
  }

  return (
    <Tooltip
      anchorElement="target"
      content={() => <span>{t('ai_disabled_message')}</span>}
      openDelay={0}
      position="top"
    >
      <div title={t('ai_disabled_message')}>{formContent}</div>
    </Tooltip>
  )
}
